import type {
    ApiCart,
    ApiCartData,
    ApiFullCartItem,
} from '../../../microApps/common/api/cart/responses/cartResponses';
import type {
    EcomPromotion,
    FixedEcomPromotion,
    MultiBuyEcomPromotion,
    PercentageEcomPromotion,
} from '../../../microApps/common/models/domain/ecommerce/promotion/promotion';
import type {
    CartItemState,
    CartRecipe,
    CartSection,
    CartSummaryState,
    RootState,
} from '../../../microApps/common/store/structureDefinitions/cartState';
import type { ApiProduct } from '../../../models/ecommerce/product/ApiProduct';

import { cartECouponAdapter } from '../../../microApps/common/store/adapters/cartECouponAdapters';
import { productsAdapter } from '../../../microApps/common/store/adapters/productsAdapter';
import { recipesAdapter } from '../../../microApps/common/store/adapters/recipesAdapter';
import { getTopProductSection } from '../../../microApps/common/utils/productUtils';
import { DefaultProductSection } from '../../../models/ecommerce/product/productSectionModel';
import { Helpers } from '../../../utility/helpers';

type CartState = Expand<RootState['cart']>;

const mapCartItemState = (cartItem: ApiFullCartItem) => {
    const itemState: CartItemState = {
        recipeMetaData: cartItem.recipeData && {
            code: cartItem.recipeData.code,
            substituteIdentifier: cartItem.recipeData.substituteIdentifier,
            substituteProductsAmount: cartItem.recipeData.substituteProductsAmount,
            recipeQuantity: cartItem.recipeData.recipeQuantity,
            includedInRecipes: cartItem.recipeData.includedInRecipes,
        },
        partOfRecipe: cartItem.partOfRecipe,
        packageSizeInformation: cartItem.packageSizeInformation,
        image: cartItem.image,
        manufacturer: cartItem.manufacturer,
        variantName: cartItem.variantName,
        productUrl: cartItem.productUrl,
        recipeData: cartItem.recipeData,
        productId: cartItem.productId,
        id: cartItem.id,
        variantId: cartItem.variantId,
        quantity: cartItem.quantity,
        discount: cartItem.discount,
        itemReplaceable: cartItem.itemReplaceable,
        name: cartItem.name,
        notBuyable: cartItem.notBuyable,
        piecePrice: cartItem.piecePrice,
        priceData: cartItem.priceData,
        recycleFeePiecePriceData: cartItem.recycleFeePiecePriceData,
        recycleFeeTotalPriceData: cartItem.recycleFeeTotalPriceData,
        totalPriceData: cartItem.totalPriceData,
        totalPriceWithoutDiscount: cartItem.totalPriceWithoutDiscount,
        variants: cartItem.variants,
    };

    return itemState;
};

const mapSummaryState = (cartData: ApiCartData) => {
    const summaryState: CartSummaryState = {
        totalPriceData: cartData.totalPriceData,
        productSumPriceData: cartData.productSumPriceData,
        totalProductPriceWithDiscounts: cartData.totalProductPriceWithDiscounts,
        productSumWithoutDiscountPriceData: cartData.productSumWithoutDiscountPriceData,
        extraAmountToBeReserved: cartData.extraAmountToBeReserved,
        cartId: cartData.cartId,
        leftToCheckout: cartData.leftToCheckout,
        minimumShoppingValue: cartData.minimumShoppingValue,
        quantity: cartData.quantity,
        tax: cartData.tax,
        totalTaxValues: cartData.totalTaxValues,
        totalDiscountPriceData: cartData.totalDiscountPriceData,
        productDiscountPriceData: cartData.productDiscountPriceData,
        totalPriceExclReservationFee: cartData.totalPriceExclReservationFee,
        delivery: cartData.deliveryMode,
        totalDepositSum: cartData.totalDepositSum,
        deliveryFeePriceData: cartData.deliveryFeePriceData,
        appliedVouchers: cartData.appliedVouchers,
        appliedOrderPromotions: cartData.appliedOrderPromotions,
        postCode: cartData.postCode,
    };

    return summaryState;
};

const mapPromoState = (promo: EcomPromotion) => {
    switch (promo.type) {
        case 'FIXED_PRICE':
            return {
                identifier: promo.identifier,
                description: promo.description,
                isMemberPrice: promo.isMemberPrice,
                maxUseText: promo.maxUseText,
                maxUse: promo.maxUse,
                type: promo.type,
                productId: promo.productId,
                variantId: promo.variantId,
                fixedPiecePrice: promo.fixedPiecePrice,
                fixedPrice: promo.fixedPrice,
            } as FixedEcomPromotion;
        case 'MULTIBUY':
            return {
                identifier: promo.identifier,
                description: promo.description,
                isMemberPrice: promo.isMemberPrice,
                maxUseText: promo.maxUseText,
                maxUse: promo.maxUse,
                type: promo.type,
                productId: promo.productId,
                variantId: promo.variantId,
                numberOfProductRequired: promo.numberOfProductRequired,
                bundlePrice: promo.bundlePrice,
            } as MultiBuyEcomPromotion;
        case 'PERCENTAGE':
            return {
                identifier: promo.identifier,
                description: promo.description,
                isMemberPrice: promo.isMemberPrice,
                maxUseText: promo.maxUseText,
                maxUse: promo.maxUse,
                type: promo.type,
                productId: promo.productId,
                variantId: promo.variantId,
                percentageDiscount: promo.percentageDiscount,
            } as PercentageEcomPromotion;
        default:
            return promo;
    }
};

const getInitialImmutableState = (cart: ApiCart) => {
    const newCartState: CartState = {
        summary: mapSummaryState(cart.cartData),
        promos: cart.promos.map((cartPromo) => mapPromoState(cartPromo)),
        items: cart.items.map((cartItem) => mapCartItemState(cartItem)),
        itemsProductData: productsAdapter.getInitialState(),
        editedOrderItems: cart.editedOrderItems.map((cartItem) => mapCartItemState(cartItem)),
        recipes: recipesAdapter.getInitialState(),
        sections: [],
        cartLoading: {
            isLoading: false,
            isInitialized: true,
            isProductDataAvailable: true, // because the product data was resolved before, this will change after cartfacade is gone
        },
        editOrder: {
            isInEditOrderMode: cart.cartData.isInEditOrderMode,
            editOrderNumber: cart.cartData.editOrderNumber,
            editOrderTimeout: cart.cartData.editOrderTimeout,
            editOrderSummary: cart.cartData.editOrderSummary,
            paymentExpirationDate: cart.cartData.paymentExpirationDate,
        },
        timeslot: cart.cartData.timeslot,
        commentAboutCustomer: cart.cartData.commentAboutCustomer || null,
        ecoupons: mapCartEcoupons(cart.ecoupons),
    };

    return newCartState;
};

const normalizeRecipeData = (cart: ApiCart) => {
    let entityState = recipesAdapter.getInitialState();

    const recipes = cart?.recipes?.map<CartRecipe>((cartRecipe) => ({
        code: cartRecipe.code,
        type: cartRecipe.type,
        calculationType: cartRecipe.calculationType,
        prioritizationType: cartRecipe.prioritizationType,
        group: cartRecipe.group ?? 'recipe',
        name: cartRecipe.name,
        portions: cartRecipe.portions,
        productsMap: cartRecipe.productsMap,
        sharedProductsMap: cartRecipe.sharedProductsMap,
        imageUrl: cartRecipe.imageUrl,
    }));

    entityState = recipesAdapter.setAll(entityState, recipes);
    return entityState;
};

const normalizeRecipeCartItemMetadata = (newCartStateItems: ApiFullCartItem[]): CartItemState[] => {
    const result = newCartStateItems.map((item: ApiFullCartItem): CartItemState => {
        if (!item.recipeData) return item;

        const { recipeData, ...relevantFields } = item;
        return {
            ...relevantFields,
            recipeMetaData: {
                code: recipeData.code,
                substituteIdentifier: recipeData.substituteIdentifier,
                substituteProductsAmount: recipeData.substituteProductsAmount,
                recipeQuantity: recipeData.recipeQuantity,
                includedInRecipes: recipeData.includedInRecipes,
            },
        };
    });
    return result;
};

const normalizeSectionData = (
    cartItems: ApiFullCartItem[],
    products: ApiProduct[],
): CartSection[] => {
    const cartSectionsDictionary: Record<number, CartSection> = {};
    const cartSections: CartSection[] = [];
    const otherSection: CartSection = {
        sectionId: DefaultProductSection.ID,
        name: DefaultProductSection.NAME,
        productIds: [],
    };

    try {
        cartItems.forEach((item) => {
            const connectedProduct = products.find(
                (product) => item.productId === product.identifier,
            );

            // item does not have connected product, add to other section
            if (!connectedProduct && otherSection.productIds) {
                otherSection.productIds.push(item.productId.toString());
                return;
            }

            // item does not have any sections, add to other section
            if (connectedProduct) {
                const productSection = getTopProductSection(connectedProduct);
                if (productSection.id === DefaultProductSection.ID && otherSection.productIds) {
                    otherSection.productIds.push(item.productId.toString());
                    return;
                }

                // eslint-disable-next-line no-prototype-builtins
                if (cartSectionsDictionary.hasOwnProperty(productSection.id)) {
                    cartSectionsDictionary[productSection.id].productIds?.push(
                        item.productId.toString(),
                    );
                } else {
                    cartSectionsDictionary[productSection.id] = {
                        sectionId: productSection.id,
                        name: productSection.name,
                        productIds: [item.productId.toString()],
                    };
                }
            }
        });

        Object.values(cartSectionsDictionary).forEach((section) => {
            cartSections.push(section);
        });

        const sortedSections = cartSections.sort((a, b) =>
            Helpers.alphabeticSortAscPredicate(a.name, b.name),
        );

        if (otherSection.productIds && otherSection.productIds.length > 0) {
            sortedSections.push(otherSection);
        }

        return sortedSections;
    } catch {
        // if anything fails dont care about sections, pack everything into Other section;
        return [
            {
                sectionId: DefaultProductSection.ID,
                name: DefaultProductSection.NAME,
                productIds: cartItems.map((items) => items.productId.toString()),
            },
        ];
    }
};

export const normalizeCartToReduxState = (cart: ApiCart, products: ApiProduct[]) => {
    const newCartState: CartState = getInitialImmutableState(cart);

    const recipes = normalizeRecipeData(cart);
    const sections = normalizeSectionData(newCartState.items, products);
    const items = normalizeRecipeCartItemMetadata(newCartState.items);

    const normalizedState: CartState = {
        summary: {
            ...newCartState.summary,
            quantity: items.reduce((acc, curr) => acc + curr.quantity, 0),
        },
        items,
        itemsProductData: productsAdapter.setAll(newCartState.itemsProductData, products),
        editedOrderItems: newCartState.editedOrderItems,
        recipes,
        sections,
        promos: newCartState.promos,
        editOrder: newCartState.editOrder,
        cartLoading: newCartState.cartLoading,
        timeslot: newCartState.timeslot,
        commentAboutCustomer: cart.cartData.commentAboutCustomer || null,
        ecoupons: newCartState.ecoupons,
    };

    return normalizedState;
};

const mapCartEcoupons = (ecoupons: ApiCart['ecoupons']): CartState['ecoupons'] => {
    return cartECouponAdapter.setAll(
        cartECouponAdapter.getInitialState(),
        ecoupons.map((ecoupon) => ({
            couponId: ecoupon.couponId,
            title: ecoupon.title,
            active: ecoupon.active,
            expectedValue: ecoupon.expectedValue,
            expirationTime: ecoupon.expirationTime,
            triggeredPromotion: ecoupon.triggeredPromotion,
            notAppliedHint: ecoupon.notAppliedHint,
            freeShipping: ecoupon.freeShipping,
            offerType: ecoupon.offerType,
            isDirty: ecoupon.isDirty,
        })),
    );
};
